import { useCallback, useRef, useState } from 'react';
import { AtariMatesAnalysis } from './use-analyze-view-port';

interface ScrollOptions {
  onScrollProgress?: (
    progress: number,
    currentPosition: number,
    totalHeight: number
  ) => void;
  scrollDelay?: number;
  stepSize?: number;
  overlapPixels?: number;
  captureScreenshots?: boolean; // Whether to capture screenshots
  screenshotCapture?: () => Promise<File | null>; // Screenshot function to use
  viewportAnalyzer?: () => Promise<AtariMatesAnalysis>;
}

interface ScrollPosition {
  x: number;
  y: number;
  viewportHeight: number;
  documentHeight: number;
  isLastStep: boolean;
  stepIndex: number;
  screenshot?: File; // Screenshot captured at this position
  viewportAnalysis?: AtariMatesAnalysis | null;
  scroll_to?: number;
}

interface ScrollInfo {
  currentX: number;
  currentY: number;
  viewportWidth: number;
  viewportHeight: number;
  documentWidth: number;
  documentHeight: number;
  canScrollVertically: boolean;
  canScrollHorizontally: boolean;
}

interface UsePageScrollerReturn {
  // State
  isScrolling: boolean;
  scrollProgress: number;

  // Methods
  scrollThroughPage: (options?: ScrollOptions) => Promise<ScrollPosition[]>;
  restoreOriginalPosition: () => Promise<void>;
  scrollToPosition: (x: number, y: number, delay?: number) => Promise<void>;
  getCurrentScrollInfo: () => ScrollInfo;
  calculateScrollPositions: (overlapPixels?: number) => number[];
}

export const usePageScroller = (): UsePageScrollerReturn => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Use refs to store original position without causing re-renders
  const originalScrollX = useRef(0);
  const originalScrollY = useRef(0);

  // Wait for content to load (images, etc.)
  const waitForContentLoad = useCallback(
    async (timeout: number = 1000): Promise<void> => {
      return new Promise((resolve) => {
        const images = Array.from(document.images);
        const unloadedImages = images.filter((img) => !img.complete);

        if (unloadedImages.length === 0) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const totalCount = unloadedImages.length;
        const timeoutId = setTimeout(resolve, timeout);

        const onImageLoad = () => {
          loadedCount++;
          if (loadedCount >= totalCount) {
            clearTimeout(timeoutId);
            resolve();
          }
        };

        unloadedImages.forEach((img) => {
          if (img.complete) {
            onImageLoad();
          } else {
            img.addEventListener('load', onImageLoad, { once: true });
            img.addEventListener('error', onImageLoad, { once: true });
          }
        });
      });
    },
    []
  );

  // Wait for scroll animation to complete and content to settle
  const waitForScrollComplete = useCallback(
    async (delay: number): Promise<void> => {
      return new Promise((resolve) => {
        setTimeout(() => {
          waitForContentLoad(500).then(resolve);
        }, delay);
      });
    },
    [waitForContentLoad]
  );

  const smoothScrollToPosition = useCallback(
    async (x: number, y: number, duration: number = 800): Promise<void> => {
      return new Promise((resolve) => {
        // Store original scroll behavior
        const originalBehavior = document.documentElement.style.scrollBehavior;

        // Enable smooth scrolling
        document.documentElement.style.scrollBehavior = 'smooth';

        // Scroll to target position
        window.scrollTo(x, y);

        // Wait for animation to complete
        setTimeout(() => {
          // Restore original scroll behavior
          document.documentElement.style.scrollBehavior =
            originalBehavior || 'auto';

          // Additional wait for content to load
          waitForContentLoad(300).then(resolve);
        }, duration);
      });
    },
    [waitForContentLoad]
  );

  // Get current scroll information
  const getCurrentScrollInfo = useCallback((): ScrollInfo => {
    return {
      currentX: window.scrollX,
      currentY: window.scrollY,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      documentWidth: Math.max(
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth,
        document.body.scrollWidth,
        document.body.offsetWidth
      ),
      documentHeight: Math.max(
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
        document.body.scrollHeight,
        document.body.offsetHeight
      ),
      canScrollVertically:
        document.documentElement.scrollHeight > window.innerHeight,
      canScrollHorizontally:
        document.documentElement.scrollWidth > window.innerWidth,
    };
  }, []);

  // Calculate optimal scroll positions for the current page
  const calculateScrollPositions = useCallback(
    (overlapPixels: number = 50): number[] => {
      const info = getCurrentScrollInfo();
      const positions: number[] = [];

      if (!info.canScrollVertically) {
        return [0]; // Only one position needed if page doesn't scroll
      }

      let currentY = 0;
      const stepSize = info.viewportHeight - overlapPixels;

      while (currentY < info.documentHeight) {
        positions.push(currentY);

        if (currentY + info.viewportHeight >= info.documentHeight) {
          break; // Last position reached
        }

        currentY += stepSize;
      }

      return positions;
    },
    [getCurrentScrollInfo]
  );

  // Scroll to a specific position
  const scrollToPosition = useCallback(
    async (x: number, y: number, delay: number = 300): Promise<void> => {
      window.scrollTo(x, y);
      await waitForScrollComplete(delay);
    },
    [waitForScrollComplete]
  );

  // Main scroll through page function - SIMPLIFIED for loop processing only
  const scrollThroughPage = useCallback(
    async (options: ScrollOptions = {}): Promise<ScrollPosition[]> => {
      const {
        onScrollProgress,
        // scrollDelay = 300,
        stepSize,
        overlapPixels = 50,
        captureScreenshots = false,
        screenshotCapture,
        viewportAnalyzer,
      } = options;

      if (isScrolling) {
        throw new Error('Already scrolling through page');
      }

      try {
        setIsScrolling(true);
        setScrollProgress(0);

        // Store original position
        originalScrollX.current = window.scrollX;
        originalScrollY.current = window.scrollY;

        // Get page dimensions
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.body.scrollHeight,
          document.body.offsetHeight
        );

        const viewportHeight = window.innerHeight;
        const actualStepSize = stepSize || viewportHeight;

        const positions: ScrollPosition[] = [];
        let currentY = 0;
        let stepIndex = 0;

        while (currentY < documentHeight) {
          // Calculate if this is the last step
          const remainingHeight = documentHeight - currentY;
          const isLastStep = remainingHeight <= actualStepSize;

          // Scroll to position
          //   window.scrollTo(0, currentY);

          //   // Wait for scroll to complete
          //   await waitForScrollComplete(scrollDelay);

          await smoothScrollToPosition(0, currentY, 1500);

          let viewportAnalysis: AtariMatesAnalysis | null = null;
          if (viewportAnalyzer) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            try {
              viewportAnalysis = await viewportAnalyzer();
            } catch (error) {
              console.error(
                `❌ Failed to analyze viewport at position ${stepIndex + 1}:`,
                error
              );
            }
          }

          // Capture screenshot if requested and function provided
          let screenshot: File | undefined = undefined;
          if (captureScreenshots && screenshotCapture) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            try {
              const capturedScreenshot = await screenshotCapture();
              if (capturedScreenshot) {
                // Create filename with position info
                const fileName = `scroll-screenshot-${stepIndex + 1}-y${currentY}.png`;
                screenshot = new File([capturedScreenshot], fileName, {
                  type: capturedScreenshot.type,
                });
                // console.log(
                //   `📸 Captured screenshot for position ${stepIndex + 1}: ${fileName}`
                // );
              }
            } catch (error) {
              console.error(
                `❌ Failed to capture screenshot at position ${stepIndex + 1}:`,
                error
              );
            }
          }

          // Record position info with screenshot
          const position: ScrollPosition = {
            x: window.scrollX,
            y: window.scrollY,
            viewportHeight,
            documentHeight,
            isLastStep,
            stepIndex,
            screenshot, // Include screenshot if captured
            viewportAnalysis,
            scroll_to: currentY,
          };

          positions.push(position);

          // Calculate and update progress
          const progress = Math.min(
            (currentY + viewportHeight) / documentHeight,
            1
          );
          setScrollProgress(Math.round(progress * 100));

          // Report progress to callback
          if (onScrollProgress) {
            onScrollProgress(progress, currentY, documentHeight);
          }

          // Calculate next scroll position
          if (isLastStep) {
            break;
          }

          currentY += actualStepSize - overlapPixels;
          stepIndex++;

          // Safety check
          if (stepIndex > 100) {
            console.warn('Maximum scroll steps reached (100), stopping');
            break;
          }
        }
        setScrollProgress(100);

        return positions;
      } catch (error) {
        console.error('Error during page scrolling:', error);
        throw error;
      } finally {
        setIsScrolling(false);
        // Reset progress after a delay
        setTimeout(() => setScrollProgress(0), 1000);
      }
    },
    [isScrolling, smoothScrollToPosition]
  );

  // Restore original scroll position
  const restoreOriginalPosition = useCallback(async (): Promise<void> => {
    if (isScrolling) {
      console.warn('Cannot restore position while scrolling is in progress');
      return;
    }
    await smoothScrollToPosition(
      originalScrollX.current,
      originalScrollY.current,
      800
    );
  }, [isScrolling, smoothScrollToPosition]);

  return {
    // State
    isScrolling,
    scrollProgress,

    // Methods
    scrollThroughPage,
    restoreOriginalPosition,
    scrollToPosition,
    getCurrentScrollInfo,
    calculateScrollPositions,
  };
};
